<script setup lang="ts" generic="T extends string">
/**
 * Sélecteur sur mesure : un champ qui ouvre un sheet avec la liste des options (recherche
 * facultative). Jamais de `<select>` natif. La liste suit le clavier et les lecteurs d'écran.
 */
import {
  ListboxContent,
  ListboxFilter,
  ListboxItem,
  ListboxItemIndicator,
  ListboxRoot,
} from 'reka-ui'
import { computed, ref, useId } from 'vue'

import { fr } from '@/copy/fr'

import type { IconName } from '../icons/registry'
import Field from './Field.vue'
import Icon from './Icon.vue'
import Sheet from './Sheet.vue'

export interface SelectOption<V extends string> {
  value: V
  label: string
  description?: string
  icon?: IconName
}

const props = withDefaults(
  defineProps<{
    label: string
    options: readonly SelectOption<T>[]
    placeholder?: string
    hint?: string
    error?: string
    searchable?: boolean
  }>(),
  { placeholder: undefined, hint: undefined, error: undefined, searchable: false },
)

const model = defineModel<T | undefined>()
const id = useId()
const open = ref(false)
const query = ref('')

const selected = computed(() => props.options.find((option) => option.value === model.value))

const filtered = computed(() => {
  const needle = query.value
    .trim()
    .toLocaleLowerCase('fr-FR')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
  if (needle === '') return props.options
  return props.options.filter((option) =>
    option.label
      .toLocaleLowerCase('fr-FR')
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .includes(needle),
  )
})

function choose(value: unknown) {
  const option = props.options.find((candidate) => candidate.value === value)
  if (!option) return
  model.value = option.value
  open.value = false
  query.value = ''
}
</script>

<template>
  <Field :id="id" :label="label" :hint="hint" :error="error">
    <template #default="{ describedBy }">
      <button
        :id="id"
        type="button"
        :aria-describedby="describedBy"
        aria-haspopup="dialog"
        :aria-expanded="open"
        :aria-invalid="error ? 'true' : undefined"
        :class="[
          'flex h-control w-full items-center gap-3 rounded-control bg-surface px-4 text-left transition-colors duration-80 active:bg-surface-strong',
          error ? 'outline-2 outline-offset-2 outline-danger' : '',
        ]"
        @click="open = true"
      >
        <Icon v-if="selected?.icon" :name="selected.icon" class="text-ink-muted" />
        <span :class="['flex-1 truncate text-body', selected ? 'text-ink' : 'text-ink-muted']">
          {{ selected?.label ?? placeholder ?? fr.ui.choose }}
        </span>
        <Icon name="chevron-down" class="text-ink-muted" />
      </button>
    </template>
  </Field>

  <Sheet v-model:open="open" :title="label">
    <ListboxRoot :model-value="model" class="flex flex-col gap-3" @update:model-value="choose">
      <div
        v-if="searchable"
        class="flex h-control items-center gap-3 rounded-control bg-surface px-4 focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-accent"
      >
        <Icon name="search" class="text-ink-muted" />
        <ListboxFilter
          v-model="query"
          :placeholder="fr.ui.search"
          class="h-full min-w-0 flex-1 bg-transparent text-input text-ink outline-none placeholder:text-ink-muted"
        />
      </div>
      <ListboxContent class="flex flex-col">
        <ListboxItem
          v-for="option in filtered"
          :key="option.value"
          :value="option.value"
          class="flex min-h-control cursor-pointer items-center gap-3 border-b border-line px-1 py-3 outline-none last:border-b-0 data-[highlighted]:bg-surface"
        >
          <Icon v-if="option.icon" :name="option.icon" :size="24" class="text-ink-muted" />
          <span class="flex flex-1 flex-col gap-0.5">
            <span class="text-body text-ink">{{ option.label }}</span>
            <span v-if="option.description" class="text-caption text-ink-muted">{{
              option.description
            }}</span>
          </span>
          <ListboxItemIndicator class="text-accent-strong">
            <Icon name="check" />
          </ListboxItemIndicator>
        </ListboxItem>
        <p v-if="filtered.length === 0" class="py-6 text-center text-label text-ink-muted">
          {{ fr.ui.noResult }}
        </p>
      </ListboxContent>
    </ListboxRoot>
  </Sheet>
</template>
